<template>
  <b-container class="bopmodel-setup mt-5" fluid style="padding: 0 5%;">
    <div
      v-if="canShowExportLoader"
      class="bopmodel-loader d-flex justify-content-center mb-3 height-100vh "
    >
      <b-spinner label="Loading..." class="margin-auto"></b-spinner>
    </div>
    <h1>{{ $t("bopModelSetup.title") }}</h1>
    <b-card class="default">
      <b-row>
        <b-col md="4">
          <b-form-group
            id="site-group"
            :label="$t('bopModelSetup.bopModelSelect')"
            label-for="selectBopModel-select"
          >
            <b-form-select
              id="selectBopModel-select"
              v-model="selectedBopModel"
              :options="bopModels"
              text-field="translation.name"
              value-field="bopModel"
              @change="handleBopModelChange"
            ></b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="4">
          <b-form-group
            id="rubrique-group"
            :label="$t('bopModelSetup.rubriqueSelect')"
            label-for="rubrique-select"
          >
            <b-form-select
              id="rubrique-select"
              v-model="selectedRubrique"
              :options="rubriqueOptions"
              @change="handleRubriqueChange"
            ></b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="4">
          <div v-if="tableRubrique === rubriques.OperationDiverse">
            <b-form-group :label="$t('bopModelSetup.TypeTravaux')">
              <b-form-checkbox-group
                id="typetravaux-group"
                v-model="selectedTypeTravaux"
                :options="typeTravaux"
                name="typetravaux"
                text-field="name"
                value-field="id"
                @change="handleTypeTravauxChange"
              ></b-form-checkbox-group>
            </b-form-group>
          </div>

          <b-form-group
            v-else-if="
              tableRubrique === rubriques.CoefficientDimensions &&
                tableBopModel.id
            "
            id="rubrique-group"
            :label="$t('bopModelSetup.coefficientType')"
            label-for="coefficient-select"
          >
            <b-form-select
              id="coefficient-select"
              v-model="selectedCoefficientId"
              :options="coefficientTypes"
              text-field="name"
              value-field="id"
              @change="handleCoefficientIdChange"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <template v-if="tableBopModel.id">
      <template
        v-if="
          (!tableRubrique ||
            (tableRubrique === rubriques.CoefficientDimensions &&
              !tableCoefficientId)) &&
            exportBopModelUrl
        "
      >
        <div class="d-flex mt-5 justify-content-end">
          <b-button
            variant="secondary"
            @click="handleImport"
            :disabled="!isCentralAdministrator"
          >
            {{ $t("bopModelSetup.importBopModel").toString() }}
          </b-button>
          <b-button
            @click="exportBopModelUrl"
            variant="secondary"
            :disabled="!isCentralAdministrator"
          >
            {{ $t("bopModelSetup.exportBopModel").toString() }}
          </b-button>
        </div>
      </template>
      <template
        v-if="
          tableRubrique === rubriques.OperationPrincipal ||
            tableRubrique === rubriques.OperationDiverse
        "
      >
        <OperationsSetup
          :key="
            `${tableRubrique}_${tableTypeTravaux.length}_${tableBopModel.id}_${$i18n.locale}`
          "
          :bopModelId="tableBopModel.id"
          :bopModelKey="tableBopModel.key"
          :operationKey="tableRubrique"
          :typeTravauxFilter="tableTypeTravaux"
        />
      </template>
      <template
        v-else-if="
          tableRubrique === rubriques.CoefficientDimensions &&
          tableCoefficientId
        "
      >
        <CoefficientOptionPerSiteAndWorkType
          v-if="isEnvSpecific"
          :key="`${tableCoefficientId}_${$i18n.locale}`"
          :bopModelId="tableBopModel.id"
          :bopModelKey="tableBopModel.key"
          :coefficient-metadata="coefficientMetadata"
          :coefficientId="tableCoefficientId"
        />
        <CoefficientDimensionsSetup
          v-else-if="isNotType5Coefficient"
          :key="`${tableCoefficientId}_${$i18n.locale}`"
          :bopModelId="tableBopModel.id"
          :bopModelKey="tableBopModel.key"
          :coefficient-metadata="coefficientMetadata"
          :coefficientId="tableCoefficientId"
        />
        <CoefficientPerSiteSetup
          v-else-if="isType5Coefficient"
          :key="`${tableCoefficientId}_${$i18n.locale}`"
          :bopModelId="tableBopModel.id"
          :bopModelKey="tableBopModel.key"
          :coefficientId="tableCoefficientId"
        />
      </template>
      <template v-else-if="tableRubrique === rubriques.Exceptions">
        <ExceptionSetup
          :key="`${tableBopModel.id}_${$i18n.locale}`"
          :bopModelId="tableBopModel.id"
          :bopModelKey="tableBopModel.key"
        />
      </template>
      <template v-else-if="tableRubrique === rubriques.ReferenceFourniture">
        <ReferenceFournitures
          :key="`${tableBopModel.id}_${$i18n.locale}`"
          :bopModelId="tableBopModel.id"
        />
      </template>
      <template v-else-if="tableRubrique === rubriques.ComplementsCoutsSupp">
        <ComplementsCoutsSuppAdministration
          :key="`${tableBopModel.id}_${$i18n.locale}`"
        />
      </template>
    </template>
    <ImportBopModelModal ref="importBopModelModal" />
  </b-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import BopModelService from "@/services/BopModelService";
import TypeTravauxService from "@/services/TypeTravauxService";
import CoefficientService from "@/services/CoefficientService";
import {
  BopModelEnum,
  CoefficientTypeEnum,
  RubriqueEnum,
  UserRoleEnum,
} from "@/utils/Enums";
import ImportBopModelModal from "@/components/BopModelSetup/ImportBopModelModal.vue";
import ExportBopModelHelper from "@/utils/ExportHelper";
import CoefficientOptionPerSiteAndWorkType from "@/components/BopModelSetup/CoefficientOptionPerSiteAndWorkType.vue";

type Data = {
  columns: Array<Object>;
  rows: Array<Object>;
};
const SEPARATOR_KEY = "separator";
@Component({
  components: {
    ImportBopModelModal,
    OperationsSetup: () =>
      import("@/components/BopModelSetup/OperationsSetup.vue"),
    CoefficientDimensionsSetup: () =>
      import("@/components/BopModelSetup/CoefficientDimensionsSetup.vue"),
    CoefficientPerSiteSetup: () =>
      import("@/components/BopModelSetup/CoefficientPerSiteSetup.vue"),
    ExceptionSetup: () =>
      import("@/components/BopModelSetup/ExceptionSetup.vue"),
    ReferenceFournitures: () =>
      import("@/components/BopModelSetup/ReferenceFournitures.vue"),
    ComplementsCoutsSuppAdministration: () =>
      import(
        "@/components/BopModelSetup/ComplementsCoutsSuppAdministration.vue"
      ),
    CoefficientOptionPerSiteAndWorkType: () =>
      import("@/components/BopModelSetup/CoefficientOptionPerSiteAndWorkType.vue"),
  },
})
export default class BopModelSetup extends Vue {
  public bopModels: Array<Object> = [];
  public selectedRows: Array<string> = [];
  public checkedRows: Array<string> = [];
  public selectedBopModel: any = {};
  public selectedRubrique: any = "";
  public selectedCoefficientId: string = "";
  public selectedTypeTravaux: Array<string> = [];
  public tableTypeTravaux: Array<string> = [];
  public oldTypeTravaux: Array<string> = [];
  private typeTravaux: Array<Object> = [];
  private rubriqueOptions: Array<any> = [];
  private coefficientTypes: Array<any> = [];
  private language = this.$i18n.locale;
  private lastSelectedBopModelId: string = "";
  private tableBopModel: any = "";
  private oldBopModel: any = "";
  private tableRubrique: any = "";
  private oldRubrique: any = "";
  private tableCoefficientId: any = "";
  private oldCoefficientId: any = "";
  private coefficientMetadata: any = null;
  private get canShowExportLoader(): boolean {
    return this.$store.state.showExportLoader;
  }
  public get isCentralAdministrator(): boolean {
    return (
      this.$store.getters.userRoleKey === UserRoleEnum.centralAdministrator
    );
  }
  private set canShowExportLoader(value: boolean) {
    this.$store.commit("changeShowExportLoader", value);
  }
  private get rubriques() {
    return RubriqueEnum;
  }

  private exportBopModelUrl() {
    if (!this.selectedBopModel) return;
    this.canShowExportLoader = true;
    ExportBopModelHelper.handleExport(this.selectedBopModel.key, this.language)
      .then(() => {
        this.canShowExportLoader = false;
      })
      .catch((er) => {
        console.error(er);
        this.canShowExportLoader = false;
      });
  }

  private get isType5Coefficient(): boolean {
    return (
      this.coefficientMetadata &&
      this.coefficientMetadata.coefficientType === CoefficientTypeEnum.T5
    );
  }

  private get isNotType5Coefficient(): boolean {
    return (
      this.coefficientMetadata &&
      this.coefficientMetadata.coefficientType !== CoefficientTypeEnum.T5
    );
  }

  private get isEnvSpecific(): boolean {
    return (
      this.coefficientMetadata && this.coefficientMetadata.key === "EnvKey"
    );
  }

  public async handleChange() {
    if (
      this.selectedBopModel &&
      this.selectedRubrique == RubriqueEnum.CoefficientDimensions &&
      this.selectedBopModel.id != this.lastSelectedBopModelId
    ) {
      this.lastSelectedBopModelId = this.selectedBopModel.id;
      this.coefficientTypes = (
        await CoefficientService.getConfigurableCoefficientTypes(
          this.selectedBopModel.id,
          this.$i18n.locale
        )
      ).data;
    }
  }

  public created() {
    this.loadDropDowns();
  }

  private handleImport() {
    (<ImportBopModelModal>this.$refs.importBopModelModal).openImportModal();
  }

  private handleRubriqueChange(value: any) {
    if (this.$store.state.hasBeenModified) {
      this.$bvModal
        .msgBoxConfirm(this.$t("modal.unsaved.message").toString(), {
          title: this.$t("modal.unsaved.title").toString(),
          size: "lg",
          buttonSize: "lg",
          okVariant: "primary",
          cancelVariant: "secondary",
          okTitle: this.$t("modal.unsaved.yes").toString(),
          cancelTitle: this.$t("modal.unsaved.no").toString(),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
          dialogClass: "modal-app",
        })
        .then((result) => {
          if (result) {
            this.tableRubrique = value;
            this.oldRubrique = value;
            this.$store.commit("changeHasBeenModified", false);
            this.handleChange();
          } else {
            this.selectedRubrique = this.oldRubrique;
          }
        });
    } else {
      this.tableCoefficientId = null;
      this.selectedCoefficientId = "";
      this.tableRubrique = value;
      this.oldRubrique = value;
      this.handleChange();
    }
  }

  private async handleBopModelChange(value: any) {
    const responseData = (await BopModelService.CanHaveExceptions(value.id))
      .data;
    const rubriqueIndex = this.rubriqueOptions.findIndex(
      (el) => el.value === RubriqueEnum.Exceptions
    );
    this.rubriqueOptions[
      rubriqueIndex
    ].disabled = !responseData.canHaveExceptions;
    if (
      !responseData.canHaveExceptions &&
      this.selectedRubrique === RubriqueEnum.Exceptions
    ) {
      this.selectedRubrique = null;
      this.tableRubrique = null;
    }
    if (this.$store.state.hasBeenModified) {
      this.$bvModal
        .msgBoxConfirm(this.$t("modal.unsaved.message").toString(), {
          title: this.$t("modal.unsaved.title").toString(),
          size: "lg",
          buttonSize: "lg",
          okVariant: "primary",
          cancelVariant: "secondary",
          okTitle: this.$t("modal.unsaved.yes").toString(),
          cancelTitle: this.$t("modal.unsaved.no").toString(),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
          dialogClass: "modal-app",
        })
        .then((result) => {
          if (result) {
            this.tableBopModel = value;
            this.oldBopModel = value;
            this.$store.commit("changeHasBeenModified", false);
            this.handleChange();
          } else {
            this.selectedBopModel = this.oldBopModel;
          }
        });
    } else {
      this.tableCoefficientId = null;
      this.selectedCoefficientId = "";
      this.tableBopModel = value;
      this.oldBopModel = value;
      await this.handleChange();
    }

    if (this.selectedBopModel.key === BopModelEnum.CalorifugageAppareil) {
      this.removeRubric(RubriqueEnum.ComplementsCoutsSupp);
      this.addRubric({
        text: this.$t("bopModelSetup.ReferenceFourniture").toString(),
        value: RubriqueEnum.ReferenceFourniture,
        key: this.$i18n.locale,
        disabled: false,
      });
    } else if (this.selectedBopModel.key === BopModelEnum.TuyauterieMetalique) {
      this.removeRubric(RubriqueEnum.ReferenceFourniture);
      this.addRubric({
        text: this.$t("bopModelSetup.ComplementsCoutsSupp").toString(),
        value: RubriqueEnum.ComplementsCoutsSupp,
        key: this.$i18n.locale,
        disabled: false,
      });
    } else {
      this.removeRubric(RubriqueEnum.ReferenceFourniture);
      this.removeRubric(RubriqueEnum.ComplementsCoutsSupp);
    }
  }

  private addRubric(rubric: any) {
    const rubricImportIndex = this.rubriqueOptions.findIndex(
      (el) => el.value === SEPARATOR_KEY
    );
    this.rubriqueOptions.splice(rubricImportIndex, 0, rubric);
  }

  private removeRubric(rubricKey: string) {
    const rubricIndex = this.rubriqueOptions.findIndex(
      (el) => el.value === rubricKey
    );
    if (rubricIndex !== -1) {
      this.rubriqueOptions.splice(rubricIndex, 1);
    }
  }
  private loadCoefficientMetadata(value) {
    CoefficientService.getCoefficientById(value).then((response) => {
      this.coefficientMetadata = response.data;
      this.tableCoefficientId = value;
      this.oldCoefficientId = value;
      this.selectedCoefficientId = value;
    });
  }
  private handleCoefficientIdChange(value: any) {
    if (this.$store.state.hasBeenModified) {
      this.$bvModal
        .msgBoxConfirm(this.$t("modal.unsaved.message").toString(), {
          title: this.$t("modal.unsaved.title").toString(),
          size: "lg",
          buttonSize: "lg",
          okVariant: "primary",
          cancelVariant: "secondary",
          okTitle: this.$t("modal.unsaved.yes").toString(),
          cancelTitle: this.$t("modal.unsaved.no").toString(),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
          dialogClass: "modal-app",
        })
        .then((result) => {
          if (result) {
            this.loadCoefficientMetadata(value);
            this.$store.commit("changeHasBeenModified", false);
            this.handleChange();
          } else {
            this.selectedCoefficientId = this.oldCoefficientId;
          }
        });
    } else {
      this.loadCoefficientMetadata(value);
      this.handleChange();
    }
  }

  private handleTypeTravauxChange(value: any) {
    if (this.$store.state.hasBeenModified) {
      this.$bvModal
        .msgBoxConfirm(this.$t("modal.unsaved.message").toString(), {
          title: this.$t("modal.unsaved.title").toString(),
          size: "lg",
          buttonSize: "lg",
          okVariant: "primary",
          cancelVariant: "secondary",
          okTitle: this.$t("modal.unsaved.yes").toString(),
          cancelTitle: this.$t("modal.unsaved.no").toString(),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
          dialogClass: "modal-app",
        })
        .then((result) => {
          if (result) {
            this.tableTypeTravaux = value;
            this.oldTypeTravaux = value;
            this.$store.commit("changeHasBeenModified", false);
            this.handleChange();
          } else {
            this.selectedTypeTravaux = this.oldTypeTravaux;
          }
        });
    } else {
      this.tableTypeTravaux = value;
      this.oldTypeTravaux = value;
      this.handleChange();
    }
  }

  private async loadDropDowns() {
    this.rubriqueOptions = [];
    let responseBopModel = await BopModelService.getBopModels(
      this.$i18n.locale
    );
    if (responseBopModel.status === 200) {
      this.bopModels = responseBopModel.data;
    }

    this.rubriqueOptions = [
      {
        text: this.$t("bopModelSetup.OperationPrincipale").toString(),
        value: RubriqueEnum.OperationPrincipal,
        key: this.$i18n.locale,
      },
      {
        text: this.$t("bopModelSetup.OperationDiverse").toString(),
        value: RubriqueEnum.OperationDiverse,
        key: this.$i18n.locale,
      },
      {
        text: this.$t("bopModelSetup.CoefficientDimensions").toString(),
        value: RubriqueEnum.CoefficientDimensions,
        key: this.$i18n.locale,
      },
      {
        text: this.$t("bopModelSetup.Exceptions").toString(),
        value: RubriqueEnum.Exceptions,
        key: this.$i18n.locale,
        disabled: false,
      },
    ];
    // }
    this.typeTravaux = (
      await TypeTravauxService.listTypeTravaux(this.$i18n.locale)
    ).data;
  }

  @Watch("$i18n.locale")
  private onLocaleChange() {
    this.loadDropDowns();
  }
}
</script>
<style lang="scss" scoped>
.bopmodel-setup {
  .bopmodel-loader {
    position: absolute;
    width: 100%;
    background-color: #ffffffc9;
    z-index: 9999;
  }
}
</style>
